import {inject, Injectable} from '@angular/core';
import {FormControl, FormGroup} from "@angular/forms";
import {RecordData} from "../../../types/records-table.types";
import {platformUrlsConfig, PlatformUrlType} from "../../../models/platform-urls-config";
import {LinkSettings} from "../../../types/file.types";
import {UtilityService} from "../../../services/utility.service";
import {BaseFormField, InitFormParams} from "../../../types/form-field.types";
import {FieldClassProps} from "../base-modal/types/base-modal.types";
import {IconStatus} from "../../../models/icon-status-config";

@Injectable({
  providedIn: 'root'
})
export class BaseModalService {
  private utilityService: UtilityService = inject(UtilityService);

  public initForm(formFields: Array<string>): FormGroup {
    const group: { [key: string]: FormControl } = {};

    formFields.forEach(field => {
      group[field] = new FormControl(null, []);
    });

    return new FormGroup(group);
  }

  public initFormGroup(initFormParams: Array<InitFormParams>): FormGroup {
    const group: { [key: string]: FormControl } = {};

    initFormParams.forEach(({ key, validators }) => {
      group[key] = new FormControl(null, validators || []);
    });

    return new FormGroup(group);
  }

  public getFormGroup<T extends BaseFormField>(formFields: Array<T>): FormGroup {
    const initFormParams = formFields.map(field => ({
      key: field.fieldName,
      validators: field.validators
    }));

    return this.initFormGroup(initFormParams);
  }

  public convertDates<T extends RecordData>(data: T, dateFields: Array<string>): T {
    dateFields.forEach(field => {
      if (data[field]) {
        (data as RecordData)[field] = this.utilityService.convertToDate(data[field]);
      }
    });

    return data;
  }

  public convertDateFieldsToRussianFormat<T extends RecordData>(data: T, dateFields: Array<string>): T {
    dateFields.forEach(field => {
      if (data[field]) {
        (data as RecordData)[field] = this.utilityService.parseTimeStringToDate(data[field] as string);
      }
    });

    return data;
  }

  public convertTimeFieldsToTimeString<T extends RecordData>(data: T, timeFields: Array<string>): T {
    timeFields.forEach(field => {
      if (data[field]) {
        (data as RecordData)[field] = this.utilityService.formatDateTimeToRussianStyle(data[field] as string);
      }
    });

    return data;
  }

  public getRecordCalendarId(recordData: RecordData): number {
    return recordData['recordCalendarId'] ?
      Number(recordData['recordCalendarId']) :
      Number(recordData.id);
  }

  public isModalValid(recordData: RecordData, key: string): boolean {
    if (recordData[key]) {
      return !!(recordData[key] as RecordData).id
    }

    return !!recordData[key];
  }

  public getRecordId(recordData: RecordData, key: string): number {
    return Number((recordData[key] as RecordData).id);
  }

  public initiateCall(key: string, formGroup: FormGroup) {
    const value = formGroup.get(key)?.value;
    if (!value) { return; }

    const phoneNumber = value.toString().trim();
    if (!this.utilityService.isValidPhoneNumber(phoneNumber)) { return; }

    const href = `tel:${phoneNumber}`;
    this.createLink({
      href,
      target: '_self'
    });
  }

  public openUrlInNewTab(key: string, formGroup: FormGroup): void {
    const value = formGroup.get(key)?.value?.trim();
    if (!value) { return; }

    const externalUrl  = this.getExternalUrl(value, key);
    if (!externalUrl ) { return; }

    this.createLink({
      href: externalUrl,
      target: '_blank',
      rel: 'noopener noreferrer'
    });
  }

  public setFieldStyles(fieldClassProps: FieldClassProps): Record<string, string> {
    const { formControlName = '', formGroup, isEditModeActive = false } = fieldClassProps;

    const isActive = this.utilityService.isActiveLinkRowForUrlType(formGroup?.get(formControlName)?.value, isEditModeActive);
    return {
      'cursor': isActive ? 'pointer' : 'default',
      'pointer-events': isActive ? 'auto' : ''
    };
  }

  public handleValueClick(fieldClassProps: FieldClassProps): void {
    const { formControlName, formGroup, isEditModeActive } = fieldClassProps;
    if (isEditModeActive) { return; }

    if (!formControlName || !formGroup) { return; }

    const value = formGroup.get(formControlName)?.value.trim();
    if (!value) { return; }
    window.open(value as string, '_blank')?.focus();
  }

  public fetchIconStatus(iconStatus: IconStatus | undefined | null): IconStatus {
    return iconStatus ?? 'ERROR';
  }

  private getExternalUrl(input: string, key: string): string | null {
    if (this.isExternalUrl(input)) { return input; }

    const urlGenerator = platformUrlsConfig[key as PlatformUrlType];
    if (urlGenerator) { return urlGenerator(input); }

    return null;
  }

  // проверяет, начинается ли входная строка с "http://" или "https://".
  private isExternalUrl(input: string): boolean {
    return /^https?:\/\//.test(input);
  }

  private createLink(settings: LinkSettings) {
    if (!settings) { return; }

    const { href, target = '', rel = ''  } = settings;
    const link = document.createElement('a');
    link.href = href;
    link.target = target;
    link.rel = rel;
    link.click();
  }
}
